import { ButtonBase, Stack, styled, Tooltip } from '@mui/material'
import { MapPinIcon } from '@phosphor-icons/react'
import { getMapsApiKey } from 'ComponentLibrary/src/common/environment'
import { Map, Marker } from 'ComponentLibrary/src/atoms/Map'
import { useColors } from 'ComponentLibrary/src/common/colors'
import GeographicPortfolioPopup, { GeographicPortfolioPopupCopy } from './GeographicPortfolioPopup'
import { GeoCoordinates } from 'ComponentLibrary/src/molecules/Address/types'
import { type CSSProperties, MouseEventHandler, useEffect, useState } from 'react'
import { MonetaryValueDataObjectType, ValueWithTrend } from 'ComponentLibrary/src/common/money'
import { type Address, assembleAddressFormat, assembleAssetTitle } from 'ComponentLibrary/src/common/address'
import { ContentChip, tokens } from 'ComponentLibrary'
import { Headline } from 'ComponentLibrary/src/piazza/text/Headline/Headline'
import { Typography } from 'ComponentLibrary/src/piazza'

export type GeographicAssetOverview = {
    coordinates: GeoCoordinates
    customer_asset_reference?: string
    address?: Partial<Address>
    assetId: string
    date?: string
    market_value?: ValueWithTrend<MonetaryValueDataObjectType>
    value_per_sqm?: MonetaryValueDataObjectType
    navigateToAsset?: React.MouseEventHandler<HTMLButtonElement>
    image?: string
    imageThumbnail?: string
}

export type GroupedAssets = {
    id: string
    label: string
    assets: GeographicAssetOverview[]
    onSelect?: () => void
}

export type GeographicPortfolioProps = {
    portfolios: GroupedAssets[]
    selectedPortfolio?: string
    label?: string
    language?: string
    popupCopy?: Partial<GeographicPortfolioPopupCopy>
    legendPosition?: 'left' | 'bottom'
    mapHeight?: CSSProperties['height']
    markerInteraction?: 'popup' | 'navigate'
}

export default function GeographicPortfolio({
    portfolios,
    selectedPortfolio,
    label,
    language,
    popupCopy,
    legendPosition = 'left',
    mapHeight,
    markerInteraction = 'popup',
}: GeographicPortfolioProps) {
    const { getColor } = useColors()
    const [map, setMap] = useState<google.maps.Map>()

    const mappedPortfolios = portfolios
        .map((group, index) => ({
            ...group,
            index,
            assets: group.assets.map(({ coordinates: { latitude, longitude }, ...asset }) => ({
                ...asset,
                coordinates: { lat: latitude, lng: longitude },
            })),
        }))
        .filter(({ id }) => selectedPortfolio === undefined || id === selectedPortfolio)

    useEffect(() => {
        if (!map) {
            return
        }
        //fit all markers in default view
        const markers = mappedPortfolios.flatMap(({ assets }) => assets.map(({ coordinates }) => coordinates))
        if (markers.length > 0) {
            const bounds = new google.maps.LatLngBounds()
            markers.forEach(({ lat, lng }) => bounds.extend(new google.maps.LatLng(lat, lng)))

            map.fitBounds(bounds, { top: 48, bottom: 8 })
            const zoom = map.getZoom()
            if (zoom && zoom > 10) {
                map.setZoom(10)
            }
        }
    }, [map, mappedPortfolios])

    if (portfolios.length === 0) {
        return null
    }

    return (
        <>
            <PortfoliosContainer $legendPosition={legendPosition} style={{ height: mapHeight !== undefined ? 'auto' : undefined }}>
                {(legendPosition === 'bottom' || (!selectedPortfolio && portfolios.length > 1)) && (
                    <Stack direction="column" gap={tokens.SizingSpacing200} sx={{ order: legendPosition === 'bottom' ? 1 : undefined }}>
                        {label && <Headline variant="headline3" title={label} />}
                        <LegendContainer $position={legendPosition}>
                            {mappedPortfolios.map(({ label, id, index, assets, onSelect }) => {
                                const content = (
                                    <>
                                        <LegendIndicator $color={getColor(index)} />
                                        <LegendLabel>
                                            <Typography variant="textXS">{label}</Typography>
                                            <ContentChip size="small" color="grey" label={assets.length.toString()} />
                                        </LegendLabel>
                                    </>
                                )
                                return (
                                    <LegendItem key={id}>
                                        {onSelect ? (
                                            <ButtonBase
                                                onClick={onSelect}
                                                sx={{
                                                    gap: tokens.SizingSpacing100,
                                                    borderRadius: tokens.SizingCornerS,
                                                    textAlign: 'left',
                                                    '&:hover, &.Mui-focusVisible': { backgroundColor: tokens.ColorSurfaceHover },
                                                }}
                                            >
                                                {content}
                                            </ButtonBase>
                                        ) : (
                                            content
                                        )}
                                    </LegendItem>
                                )
                            })}
                        </LegendContainer>
                    </Stack>
                )}
                <MapContainer style={{ height: mapHeight, flexShrink: mapHeight !== undefined ? 0 : undefined }}>
                    <Map
                        center={{ lat: 51.1657, lng: 10.4515 }} // center of Germany. Will be overwritten by fitBounds anyway.
                        onGoogleApiLoaded={({ map }) => setMap(map)}
                        hasControl={false}
                        apiKey={getMapsApiKey()}
                        language={language}
                        options={{
                            fullscreenControl: false,
                            scrollwheel: false, // Disables the hint that you should use cmd+scroll to zoom
                            //zoomControl: false // Uncomment if we make the map widget small again
                        }}
                    >
                        {mappedPortfolios.map(({ assets, index }) => {
                            return assets.map(
                                ({
                                    coordinates: { lat, lng },
                                    date,
                                    address,
                                    customer_asset_reference,
                                    market_value,
                                    value_per_sqm,
                                    image,
                                    imageThumbnail,
                                    navigateToAsset,
                                    assetId,
                                }) => {
                                    return (
                                        <GeographicPortfolioMarker
                                            key={assetId}
                                            color={getColor(index)}
                                            title={getMarkerTitle({ address, customer_asset_reference })}
                                            customer_asset_reference={customer_asset_reference}
                                            lat={lat}
                                            lng={lng}
                                            date={date}
                                            market_value={market_value}
                                            value_per_sqm={value_per_sqm}
                                            image={image}
                                            imageThumbnail={imageThumbnail}
                                            navigateToAsset={navigateToAsset}
                                            locale={language}
                                            popupCopy={popupCopy}
                                            interaction={markerInteraction}
                                        />
                                    )
                                }
                            )
                        })}
                    </Map>
                </MapContainer>
            </PortfoliosContainer>
        </>
    )
}

function getMarkerTitle({ address, customer_asset_reference }: Pick<GeographicAssetOverview, 'address' | 'customer_asset_reference'>) {
    const addressTitle = assembleAddressFormat(address ?? {}, { delimiter: ', ' })
    return assembleAssetTitle({ assetTitle: addressTitle || undefined, customer_asset_reference })
}

export type GeographicPortfolioMarkerProps = {
    title: string
    lat: number
    lng: number
    date?: string
    market_value?: ValueWithTrend<MonetaryValueDataObjectType>
    value_per_sqm?: MonetaryValueDataObjectType
    image?: string
    imageThumbnail?: string
    navigateToAsset?: MouseEventHandler<HTMLElement>
    color: string
    onClick?: () => void
    customer_asset_reference?: string
    locale?: string
    popupCopy?: Partial<GeographicPortfolioPopupCopy>
    interaction?: GeographicPortfolioProps['markerInteraction']
}

export function GeographicPortfolioMarker({
    title,
    lat,
    lng,
    date,
    market_value,
    value_per_sqm,
    image,
    imageThumbnail,
    color,
    navigateToAsset,
    onClick,
    locale,
    popupCopy,
    interaction = 'popup',
}: GeographicPortfolioMarkerProps) {
    if (interaction === 'navigate') {
        return (
            <Tooltip title={title}>
                <ButtonBase
                    aria-label={title}
                    onClick={(event) => {
                        event.stopPropagation()
                        navigateToAsset?.(event)
                    }}
                    sx={{
                        transform: 'translateY(-33%)',
                        '&:hover': { transform: 'translateY(-33%) scale(1.06)' },
                    }}
                >
                    <MapPinIcon size={tokens.SizingIconL} weight="fill" color={color} />
                </ButtonBase>
            </Tooltip>
        )
    }

    return (
        <Marker
            color={color}
            lat={lat}
            lng={lng}
            popup={
                <GeographicPortfolioPopup
                    image={image}
                    imageThumbnail={imageThumbnail}
                    date={date}
                    title={title}
                    market_value={market_value}
                    value_per_sqm={value_per_sqm}
                    navigateToAsset={navigateToAsset}
                    locale={locale}
                    copy={popupCopy}
                />
            }
            onClick={onClick}
        />
    )
}

const PortfoliosContainer = styled('div', {
    shouldForwardProp: (prop: string) => prop[0] !== '$',
})<{ $legendPosition: GeographicPortfolioProps['legendPosition'] }>(({ $legendPosition }) => ({
    display: 'flex',
    height: '100%',
    flexDirection: $legendPosition === 'bottom' ? 'column' : 'row',
    gap: $legendPosition === 'bottom' ? tokens.SizingSpacing200 : undefined,
}))

//Legend
const LegendContainer = styled('div', {
    shouldForwardProp: (prop: string) => prop[0] !== '$',
})<{ $position: GeographicPortfolioProps['legendPosition'] }>(({ $position }) => ({
    gap: tokens.SizingSpacing150,
    display: 'flex',
    ...($position === 'bottom'
        ? { flexDirection: 'row', flexWrap: 'wrap' }
        : {
              flexDirection: 'column',
              maxWidth: '150px',
              height: '100%',
              paddingRight: tokens.SizingSpacing150,
              overflowY: 'auto',
              overflowX: 'hidden',
          }),
}))

const LegendItem = styled('div')(() => ({
    display: 'flex',
    gap: tokens.SizingSpacing100,
    alignItems: 'center',
}))

const LegendLabel = styled('div')(() => ({
    display: 'flex',
    width: '100%',
    justifyContent: 'space-between',
    gap: tokens.SizingSpacing050,
    alignItems: 'center',
}))

const LegendIndicator = styled('div', {
    shouldForwardProp: (prop: string) => prop[0] !== '$',
})<{ $color: string }>(({ $color }) => ({
    minWidth: '16px', // prevent squishing of the rectangle
    width: '16px',
    height: '16px',
    backgroundColor: $color,
    borderRadius: '4px',
}))

const MapContainer = styled('div')(() => ({
    flexGrow: 1,
    minHeight: 0,
    borderRadius: '4px',
    overflow: 'hidden',
}))
